import React, { useRef, useState } from "react";
import {
  Calendar,
  Clock,
  Video,
  Languages,
  Sparkles,
  Lock,
  Users,
  Volume2,
  VolumeX,
  Star,
} from "lucide-react";
import Countdown from "./Countdown";
import BrandsMarquee from "./BrandsMarquee";
import CTAButton from "./CTAButton";

// Chair icon (inline SVG, matches reference visual)
function ChairIcon({ dim = false, size = 16 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={dim ? "#3a1414" : "#ff4d4d"}
      className="shrink-0"
      aria-hidden="true"
    >
      <path d="M6 3h12v6H6zM4 10h16v2H4zm2 3h12l-1 8h-2l-.5-5h-5l-.5 5h-2z" />
    </svg>
  );
}

function SeatsRow({ seats = 50, total = 11 }) {
  const dimmedCount = Math.max(0, total - Math.ceil((60 - seats) / 5));
  return (
    <div className="flex flex-wrap items-center justify-center gap-1.5">
      {Array.from({ length: total }).map((_, i) => (
        <ChairIcon key={i} dim={i >= dimmedCount} />
      ))}
    </div>
  );
}

export default function Hero() {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);

  const toggleMute = () => {
    setMuted((prev) => {
      const next = !prev;
      if (videoRef.current) {
        videoRef.current.muted = next;
        // Some browsers pause on programmatic unmute; keep it playing
        const p = videoRef.current.play();
        if (p && typeof p.catch === "function") p.catch(() => {});
      }
      return next;
    });
  };

  return (
    <section className="relative z-10 px-4 md:px-8 pt-8 md:pt-12 pb-14">
      <div className="hero-glow" />
      <div className="max-w-7xl mx-auto relative">
        {/* Eyebrow */}
        <p className="text-center text-[#C4FF3D] uppercase tracking-[0.28em] text-[11px] md:text-xs font-bold mb-5">
          For Coaches, Consultants, Experts &amp; High-Ticket Service Businesses
        </p>

        {/* Brand + Headline */}
        <h1 className="display-font text-center text-white font-extrabold leading-[1.02] tracking-tight mb-6">
          <span className="block text-4xl md:text-6xl lg:text-7xl mb-2">
            <span className="lime-box">AI MONEY MACHINE</span>{" "}
            <span className="text-white">
              SYSTEM<span className="text-[0.4em] text-white/50 align-top ml-0.5 tracking-normal">&trade;</span>
            </span>
          </span>
          <span className="block text-lg md:text-2xl lg:text-3xl text-white/90 font-bold mt-5 max-w-4xl mx-auto leading-snug">
            Turn Your Expertise Into a 24/7 Client Acquisition Machine Using{" "}
            <span className="text-[#C4FF3D]">AI and Content Systems</span> used by the{" "}
            <span className="text-[#C4FF3D]">Top 1%.</span>
          </span>
        </h1>

        {/* 2-COLUMN: Video (left) + Pricing/Timer Card (right) */}
        <div className="hero-grid grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          {/* LEFT: Video frame */}
          <div className="relative min-w-0">
            <div className="absolute -inset-6 bg-[radial-gradient(circle,rgba(196,255,61,0.18),transparent_60%)] blur-3xl" />
            <div className="relative">
              <div className="relative aspect-video w-full rounded-2xl overflow-hidden border border-[rgba(196,255,61,0.35)] bg-[#0e1410] shadow-[0_0_60px_rgba(196,255,61,0.15)] group">
                <video
                  ref={videoRef}
                  src="https://customer-assets.emergentagent.com/job_viral-lp-preview/artifacts/ype79h0y_YT%20ads%2002_TEJAS%20%281%29.mp4"
                  className="absolute inset-0 w-full h-full object-cover"
                  autoPlay
                  muted={muted}
                  loop
                  playsInline
                  preload="auto"
                  poster="https://customer-assets.emergentagent.com/job_viral-lp-preview/artifacts/1p29fvd1_Screenshot%202026-06-01%20at%208.33.35%E2%80%AFPM.png"
                  aria-label="AI Money Machine System workshop preview"
                />
                {/* Subtle bottom gradient for legibility if we ever overlay a caption */}
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/40 to-transparent" />

                {/* Mute / unmute toggle */}
                <button
                  type="button"
                  onClick={toggleMute}
                  className="absolute bottom-3 right-3 z-10 inline-flex items-center gap-2 px-3 py-2 rounded-full bg-[#0a0f0a]/85 border border-[rgba(196,255,61,0.35)] backdrop-blur-sm text-white text-xs md:text-sm font-semibold hover:text-[#C4FF3D] hover:border-[#C4FF3D] transition"
                  aria-label={muted ? "Unmute video" : "Mute video"}
                >
                  {muted ? (
                    <>
                      <VolumeX className="w-4 h-4" />
                      <span className="hidden sm:inline">Tap for sound</span>
                    </>
                  ) : (
                    <Volume2 className="w-4 h-4 text-[#C4FF3D]" />
                  )}
                </button>
              </div>

              {/* Mentor descriptor */}
              <div className="mt-6 rounded-2xl border border-[rgba(196,255,61,0.28)] bg-[#0e1410] px-5 md:px-7 py-4 md:py-5">
                <p className="text-center text-white/85 text-sm md:text-base leading-relaxed">
                  <span className="text-[#C4FF3D] font-extrabold tracking-wide">Nawaz Shaikh</span>{" "}
                  is one of Asia&apos;s fastest-growing AI entrepreneurs, known for building{" "}
                  <span className="text-white font-semibold">VIRALX&trade;</span>.
                  <br />
                  Scaling experts &amp; founders rapidly through AI-powered client-acquisition systems.
                </p>
              </div>

              {/* Brand marquee fills the empty space below the mentor pill on desktop */}
              <div className="mt-6">
                <BrandsMarquee variant="stacked" />
              </div>
            </div>
          </div>

          {/* RIGHT: VIP Pricing / Timer Card */}
          <div className="relative min-w-0">
            <div className="absolute -inset-1 rounded-[28px] bg-[conic-gradient(from_var(--angle),transparent_0deg,rgba(196,255,61,0.6)_60deg,transparent_120deg,transparent_240deg,rgba(196,255,61,0.35)_300deg,transparent_360deg)] opacity-70 blur-sm vip-spin" />
            <div className="relative rounded-[28px] border border-[rgba(196,255,61,0.35)] bg-gradient-to-b from-[#0c130c] to-[#0a0f0a] overflow-hidden">
              {/* Ribbon */}
              <div className="flex items-center justify-between px-5 md:px-7 py-3 border-b border-dashed border-[rgba(196,255,61,0.2)] bg-[rgba(196,255,61,0.04)]">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-[#C4FF3D]" />
                  <span className="text-[10px] md:text-xs uppercase tracking-[0.18em] font-bold text-[#C4FF3D]">
                    AI Money Machine &middot; VIP Access
                  </span>
                  <Star className="w-3.5 h-3.5 text-[#C4FF3D]" fill="#C4FF3D" />
                </div>
                <span className="inline-flex items-center gap-1 text-[10px] md:text-xs text-white/60">
                  <Lock className="w-3 h-3" />
                  Limited
                </span>
              </div>

              <div className="p-6 md:p-8">
                {/* Pricing */}
                <div className="text-center mb-6">
                  <p className="uppercase text-[11px] tracking-[0.25em] text-white/55 mb-3 font-semibold">
                    Claim Your VIP Access
                  </p>
                  <div className="flex items-baseline justify-center gap-3 md:gap-4">
                    <span className="relative display-font text-3xl md:text-4xl font-bold text-white/45">
                      &#8377;1,999
                      <span className="absolute left-0 right-0 top-1/2 h-[3px] bg-red-500 rotate-[-8deg]" />
                    </span>
                    <span className="display-font text-5xl md:text-6xl font-extrabold text-[#C4FF3D] free-pulse">
                      &#8377;199
                    </span>
                  </div>
                </div>

                {/* Countdown */}
                <Countdown />

                {/* Event details */}
                <div className="mt-7 space-y-2">
                  <DetailRow icon={<Calendar className="w-4 h-4" />} label="DATE" value="Sun, 26 July" />
                  <DetailRow icon={<Clock className="w-4 h-4" />} label="TIME" value="11:00 AM IST" />
                  <DetailRow icon={<Video className="w-4 h-4" />} label="LIVE ON" value="ZOOM" />
                  <DetailRow icon={<Languages className="w-4 h-4" />} label="LANGUAGE" value="Eng + Hin" last />
                </div>

                {/* CTA */}
                <div className="mt-7 flex flex-col items-center gap-4">
                  <CTAButton source="hero_vip_card" size="lg" block>
                    Yes! Reserve My Seat For &#8377;199
                  </CTAButton>

                  {/* Registered count */}
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[rgba(196,255,61,0.08)] border border-[rgba(196,255,61,0.25)]">
                    <Users className="w-3.5 h-3.5 text-[#C4FF3D]" />
                    <span className="text-white text-xs md:text-sm font-semibold">
                      <span className="text-[#C4FF3D] font-extrabold">8,520</span> people have registered
                    </span>
                  </div>

                  {/* Chair row + seats left */}
                  <div className="w-full pt-2">
                    <SeatsRow seats={19} />
                    <p className="text-center text-white/75 text-xs md:text-sm font-semibold mt-2.5">
                      Only <span className="text-[#ff5757] font-extrabold">19 seats</span> remain at this price
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function DetailRow({ icon, label, value, last }) {
  return (
    <div
      className={`flex items-center justify-between py-2.5 ${
        last ? "" : "border-b border-dashed border-[rgba(196,255,61,0.15)]"
      }`}
    >
      <div className="flex items-center gap-2 text-white/70">
        <span className="text-[#C4FF3D]">{icon}</span>
        <span className="uppercase tracking-wider text-[11px] md:text-xs font-semibold">{label}</span>
      </div>
      <span className="text-white font-bold text-sm md:text-base">{value}</span>
    </div>
  );
}
